import { useEffect, useState } from "react";
import { API, SUBSCRIPTION_PLAN_TYPES } from "../../../../common/Constants";
import useFetch from "../../../../hooks/useFetch";
import PreLoader from "../../../../core/common/preLoader";
import { getAPIHeaders, isNotEmpty } from "../../../../common/Utils";
import CommonSelectV2, { Option } from "../../../../core/common/commonSelectV2";
import CustomModal from "../../../../core/common/customModal";

interface Props {
  defaultCourseId?: number | undefined;
  show: boolean;
  onSuccess: () => void;
  onClose: () => void;
}

interface Subscription {
  name: string;
  description: string;
  planType: string;
  duration: number;
  isActive: boolean;
  price: number | undefined;
  currencyCode: string;
  promotionalPrice: number | undefined;
  courseIds: number[];
}

// Commercial courses will be fetched from API

const AddSubscription = ({ defaultCourseId, show, onSuccess, onClose }: Props) => {
  const [commercialCourses, setCommercialCourses] = useState<any[]>([]);

  const [subscription, setSubscription] = useState<Subscription>({
    name: "",
    description: "",
    planType: "",
    duration: 1,
    isActive: true,
    price: 0,
    currencyCode: "INR",
    promotionalPrice: 0,
    courseIds: [],
  });
  const [errorMessage, setErrorMessage] = useState<string | undefined>("");
  const { isLoading, error, callFetch } = useFetch();
  const [showMsgBox, setShowMsgBox] = useState<boolean>(false);

  // Fetch commercial courses on component mount
  useEffect(() => {
    fetchCommercialCourses();
  }, []);

  useEffect(() => {
    if (subscription?.planType === "FREE" || subscription?.planType === "DEFAULT") {
      setSubscription(prev => (
        { ...prev, promotionalPrice: 0 }
      ));
    }
  }, [subscription.planType]);

  useEffect(() => {
    if (commercialCourses && defaultCourseId) {
      const course = commercialCourses.find((course: any) => course.id === defaultCourseId);
      if (course) {
        setSubscription({
          name: course.title,
          description: course.description,
          planType: 'PREMIUM',
          duration: 1,
          isActive: true,
          price: 0,
          currencyCode: 'INR',
          promotionalPrice: 0,
          courseIds: [defaultCourseId],
        });
      }
    }
  }, [commercialCourses, defaultCourseId]);

  const fetchCommercialCourses = () => {
    callFetch(
      `${API.COURSE_ALL}?isCommercial=true`,
      'GET',
      getAPIHeaders(),
      null,
      null
    ).then((response) => {
      setCommercialCourses(response || []);
    }).catch((err) => {
      console.error('Error fetching commercial courses:', err);
    });
  };


  useEffect(() => {
    if (error) {
      if (error.response?.data) {
        setErrorMessage(error.response?.data);
      } else {
        setErrorMessage("Something went wrong please try again later.");
      }
    }
  }, [error]);

  const handleSubmit = (event: any) => {
    event.preventDefault();
    if (!isNotEmpty(subscription?.name)) {
      setErrorMessage("Please provide subscription name");
    } else if (!isNotEmpty(subscription?.description)) {
      setErrorMessage("Please provide description");
    } else if (!isNotEmpty(subscription?.planType)) {
      setErrorMessage("Please select plan type");
    } else if (!isNotEmpty(subscription?.duration)) {
      setErrorMessage("Please provide duration in months");
    } else if (!subscription?.price && subscription?.planType === 'PREMIUM') {
      setErrorMessage("Price must be specified");
    } else if (!subscription?.courseIds.length) {
      setErrorMessage("Please select at least one course");
    } else {
      setErrorMessage("");
      callFetch(API.SUBSCRIPTION_PLAN, "POST", getAPIHeaders(), null, {
        ...subscription,
      }).then(()=>{
        reset();
        setShowMsgBox(true);
        onSuccess();
      }).catch((err) => {
        console.error('Error creating subscription:', err);
      });
    }
  };

  const reset = () => {
    setSubscription({
      name: "",
      description: "",
      planType: "",
      duration: 1,
      isActive: true,
      price: 0,
      currencyCode: "INR",
      promotionalPrice: 0,
      courseIds: [],
    });
    setErrorMessage("");
    setShowMsgBox(false);
    onClose();
  };


  const courseOptions: Option[] = commercialCourses.map((course: any) => ({
    value: course.id.toString(),
    label: `${course.title} (${course.code})`,
  }));

  const renderForm = () => {
    return (
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">
                Subscription Name <span className="text-danger"> *</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={subscription?.name}
                onChange={(e) => {
                  setSubscription({
                    ...subscription,
                    name: e.target.value,
                  });
                }}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">
                Plan Type <span className="text-danger"> *</span>
              </label>
              <CommonSelectV2
                options={SUBSCRIPTION_PLAN_TYPES}
                onChange={(value: string) => {
                  setSubscription({
                    ...subscription,
                    planType: value,
                  });
                }}
                value={subscription?.planType ? { value: subscription.planType, label: subscription.planType } : null}
                placeHolder="Select Plan Type"
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="mb-3">
              <label className="form-label">
                Description <span className="text-danger"> *</span>
              </label>
              <textarea
                className="form-control"
                rows={3}
                value={subscription?.description}
                onChange={(e) => {
                  setSubscription({
                    ...subscription,
                    description: e.target.value,
                  });
                }}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">
                Duration <span className="text-muted">(in months)</span><span className="text-danger"> *</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={subscription?.duration}
                onChange={(e) => {
                  setSubscription({
                    ...subscription,
                    duration:  e.target.value ? parseInt(e.target.value) : 0,
                  });
                }}
                min="0"
                step="0.01"
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">
                Currency
              </label>
              <input
                type="text"
                className="form-control"
                value={subscription?.currencyCode}
                disabled
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">
                Price (₹)
              </label>
              <input
                type="text"
                className="form-control"
                value={subscription?.price}
                onChange={(e) => {
                  if(e.target.value){
                    setSubscription({
                      ...subscription,
                      price: parseFloat(e.target.value) || 0,
                    }) 
                  }else {
                    setSubscription({
                      ...subscription,
                      price: undefined,
                    });
                  }
                }}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">
                Promotional Price (₹)
              </label>
              <input
                type="text"
                disabled={subscription?.planType === 'FREE' || subscription?.planType === 'DEFAULT'}
                className="form-control"
                value={subscription?.promotionalPrice}
                onChange={(e) => {
                  if(e.target.value){
                    setSubscription({
                      ...subscription,
                      promotionalPrice: parseFloat(e.target.value) || 0,
                    });
                  } else {
                    setSubscription({
                      ...subscription,
                      promotionalPrice: undefined,
                    });
                  }

                }}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="mb-3">
              <label className="form-label">
                Select Courses <span className="text-danger"> *</span>
              </label>
              <CommonSelectV2
                isMulti
                options={courseOptions}
                onChange={(values: string[]) => {
                  setSubscription({
                    ...subscription,
                    courseIds: values.map(v => parseInt(v)),
                  });
                }}
                values={subscription?.courseIds.map(id => ({
                  value: id.toString(),
                  label: commercialCourses.find((c: any) => c.id === id)?.title || '',
                }))}
                placeHolder="Select Courses"
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={subscription?.isActive}
                  onChange={(e) => {
                    setSubscription({
                      ...subscription,
                      isActive: e.target.checked,
                    });
                  }}
                />
                <label className="form-check-label">
                  Active Subscription
                </label>
              </div>
            </div>
          </div>
        </div>

        {errorMessage && (
          <div className="alert alert-danger" role="alert">
            <i className="ti ti-alert-triangle me-2"></i>
            {errorMessage}
          </div>
        )}

        <div className="d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Creating...
              </>
            ) : (
              <>
                <i className="ti ti-plus me-1"></i>
                Add Subscription
              </>
            )}
          </button>
        </div>
      </form>
    );
  };

  const renderMsgBox = () => {
    return (
      <div className="pb-0">
        <div className="d-flex pb-2 justify-content-center">
          {errorMessage ? (
            <label className="text-danger fw-medium">{errorMessage}</label>
          ) : (
            <label className="text-dark fw-medium">
              Subscription created successfully
            </label>
          )}
        </div>
      </div>
    );
  };

  return (
    <CustomModal
      show={show}
      title="Add Subscription"
      onClose={onClose}
      size="lg"
      bodyContent={
        <div>
          <PreLoader showLoader={isLoading} />
          {showMsgBox ? renderMsgBox() : renderForm()}
        </div>
      }
    />
  );
};

export default AddSubscription;